import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useId } from "react";

export type RepeatFrequencyValue = "none" | "daily" | "weekly" | "monthly";
export type EndConditionType = "never" | "count" | "until";

export interface RecurrenceRuleValue {
  frequency: RepeatFrequencyValue;
  timeOfDay: string; // "HH:mm" — required when frequency !== "none"
  daysOfWeek: number[]; // 0=Sun..6=Sat — required when frequency === "weekly"
  daysOfMonth: number[]; // 1..31 — required when frequency === "monthly"
  endCondition: EndConditionType;
  count: number | null;
  until: string; // ISO date (YYYY-MM-DD) — used when endCondition === "until"
}

export const DEFAULT_RECURRENCE_RULE: RecurrenceRuleValue = {
  frequency: "none",
  timeOfDay: "",
  daysOfWeek: [],
  daysOfMonth: [],
  endCondition: "never",
  count: null,
  until: "",
};

const WEEKDAY_LABELS: { value: number; label: string; short: string }[] = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];

const END_CONDITION_OPTIONS: { value: EndConditionType; label: string }[] = [
  { value: "never", label: "Never" },
  { value: "count", label: "After N occurrences" },
  { value: "until", label: "Until a specific date" },
];

interface RecurrenceRulePickerProps {
  value: RecurrenceRuleValue;
  onChange: (next: RecurrenceRuleValue) => void;
  disabled?: boolean;
}

export function RecurrenceRulePicker({
  value,
  onChange,
  disabled = false,
}: RecurrenceRulePickerProps) {
  const idPrefix = useId();

  const update = (patch: Partial<RecurrenceRuleValue>) =>
    onChange({ ...value, ...patch });

  const toggleWeekday = (n: number) => {
    const next = value.daysOfWeek.includes(n)
      ? value.daysOfWeek.filter((d) => d !== n)
      : [...value.daysOfWeek, n].sort();
    update({ daysOfWeek: next });
  };

  const toggleDayOfMonth = (n: number) => {
    const next = value.daysOfMonth.includes(n)
      ? value.daysOfMonth.filter((d) => d !== n)
      : [...value.daysOfMonth, n].sort((a, b) => a - b);
    update({ daysOfMonth: next });
  };

  const showSubFields = value.frequency !== "none";

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-freq`}>Repeat</Label>
        <Select
          value={value.frequency}
          onValueChange={(v) =>
            update({ frequency: v as RepeatFrequencyValue })
          }
          disabled={disabled}
        >
          <SelectTrigger
            id={`${idPrefix}-freq`}
            data-testid="select-repeat-frequency"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Don't repeat</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {showSubFields && (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-time`}>
              Time of day{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (in each user's timezone)
              </span>
            </Label>
            <Input
              id={`${idPrefix}-time`}
              type="time"
              value={value.timeOfDay}
              onChange={(e) => update({ timeOfDay: e.target.value })}
              disabled={disabled}
              data-testid="input-repeat-time"
            />
            <p className="text-xs text-muted-foreground">
              Each recipient fires at this clock time in their own timezone.
              Users without a timezone fall back to UTC.
            </p>
          </div>

          {value.frequency === "weekly" && (
            <div className="space-y-2">
              <Label>Days of week</Label>
              <div className="flex flex-wrap gap-2" role="group">
                {WEEKDAY_LABELS.map((d) => {
                  const selected = value.daysOfWeek.includes(d.value);
                  return (
                    <Button
                      key={d.value}
                      type="button"
                      variant={selected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleWeekday(d.value)}
                      disabled={disabled}
                      data-testid={`weekday-${d.value}`}
                      aria-pressed={selected}
                      aria-label={d.label}
                      className="min-w-[3rem]"
                    >
                      {d.short}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {value.frequency === "monthly" && (
            <div className="space-y-2">
              <Label>Days of month</Label>
              <div className="grid grid-cols-7 gap-1.5 sm:gap-2" role="group">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                  const selected = value.daysOfMonth.includes(day);
                  return (
                    <Button
                      key={day}
                      type="button"
                      variant={selected ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleDayOfMonth(day)}
                      disabled={disabled}
                      data-testid={`day-of-month-${day}`}
                      aria-pressed={selected}
                      className={cn("h-9 w-full px-0 text-xs font-medium")}
                    >
                      {day}
                    </Button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Days that don't exist in a given month (e.g. 31 in February) are
                skipped that month.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>End condition</Label>
            <RadioGroup
              value={value.endCondition}
              onValueChange={(v) =>
                update({
                  endCondition: v as EndConditionType,
                  count: v === "count" ? (value.count ?? 10) : null,
                  until: v === "until" ? value.until : "",
                })
              }
              disabled={disabled}
            >
              {END_CONDITION_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  htmlFor={`${idPrefix}-end-${opt.value}`}
                  className="flex items-center gap-2 cursor-pointer text-sm"
                >
                  <RadioGroupItem
                    id={`${idPrefix}-end-${opt.value}`}
                    value={opt.value}
                    data-testid={`end-condition-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </RadioGroup>

            {value.endCondition === "count" && (
              <Input
                type="number"
                min={1}
                max={1000}
                value={value.count ?? 10}
                onChange={(e) =>
                  update({ count: Number(e.target.value) || null })
                }
                disabled={disabled}
                placeholder="10"
                data-testid="input-repeat-count"
                className="max-w-[160px]"
              />
            )}
            {value.endCondition === "until" && (
              <Input
                type="date"
                value={value.until}
                onChange={(e) => update({ until: e.target.value })}
                disabled={disabled}
                data-testid="input-repeat-until"
                className="max-w-[200px]"
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Returns null when the rule is valid; returns an error message otherwise.
 * UI callers can short-circuit submit with this.
 */
export function validateRecurrenceRule(
  value: RecurrenceRuleValue,
): string | null {
  if (value.frequency === "none") return null;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value.timeOfDay)) {
    return "Time of day must be in HH:mm format";
  }
  if (value.frequency === "weekly" && value.daysOfWeek.length === 0) {
    return "Pick at least one weekday for weekly repeat";
  }
  if (value.frequency === "monthly" && value.daysOfMonth.length === 0) {
    return "Pick at least one day-of-month for monthly repeat";
  }
  if (value.endCondition === "count" && (!value.count || value.count < 1)) {
    return "Occurrence count must be at least 1";
  }
  if (value.endCondition === "until" && !value.until) {
    return "Pick an end date";
  }
  return null;
}
